import React, { useState, useRef, useEffect } from 'react';
import { Box, Collapse, Stack, Typography, useTheme } from '@mui/material';
import { ExpandMore, SmartToy, CheckCircle, HourglassEmpty, RadioButtonUnchecked, Pending, Error as ErrorIcon, KeyboardDoubleArrowDown, KeyboardDoubleArrowUp } from '@mui/icons-material';
import type { SubAgentState } from '../stateConfiguration';
import MessageAgent from './MessageAgent';
import { useAiChatSessionStore } from '../AiChatContext';
import type { TodoList, TodoItem } from '@lib/models/todoListModel';
import LinerBackgroundProgress from '@lib/components/Library/LinearBackgroundProgress';

type ExpansionLevel = 0 | 1 | 2;

const MAX_TODO_ITEMS_LEVEL2 = 6;

export interface SubAgentBoxProps {
    subAgent: SubAgentState;
    compact?: boolean;
    onLinkClicked: (link: string, content: string | null) => void;
}

function StatusIcon({ status }: { status: TodoItem['status'] }) {
    switch (status) {
        case 'completed':
            return <CheckCircle sx={{ fontSize: '0.85rem' }} />;
        case 'in_progress':
            return <Pending sx={{ fontSize: '0.85rem' }} />;
        case 'failed':
            return <ErrorIcon sx={{ fontSize: '0.85rem' }} />;
        default:
            return <RadioButtonUnchecked sx={{ fontSize: '0.85rem' }} />;
    }
}

function statusColor(status: TodoItem['status']): string {
    switch (status) {
        case 'completed':
            return 'success.main';
        case 'in_progress':
            return 'primary.main';
        case 'failed':
            return 'error.main';
        default:
            return 'text.disabled';
    }
}

const SubAgentBox: React.FC<SubAgentBoxProps> = ({ subAgent, compact, onLinkClicked }) => {
    const [level, setLevel] = useState<ExpansionLevel>(compact ? 1 : 0);
    const [todoList, setTodoList] = useState<TodoList | null>(null);
    const theme = useTheme();
    const boxRef = useRef<HTMLDivElement>(null);



    const isRunning = !subAgent.completed;

    const lastToolEvent = useAiChatSessionStore((state) => state.lastToolEvent);

    useEffect(() => {
        if (
            lastToolEvent?.eventName === 'todolistUpdated' &&
            lastToolEvent.subAgentId === subAgent.id
        ) {
            try {
                const parsed: TodoList = JSON.parse(lastToolEvent.eventData);
                setTodoList(parsed);
            } catch {
                // ignore parse errors
            }
        }

        if (lastToolEvent?.eventName === 'todolistUpdated' && lastToolEvent?.eventData !== "" && !isRunning && lastToolEvent.subAgentId === subAgent.id) {
            setTodoList(null);
        }
    }, [lastToolEvent, subAgent.id]);

    useEffect(() => {
        if (level > 0 && boxRef.current) {
            boxRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [level]);

    const cycleLevel = () => {

        setLevel((prev) => {
            const ret = prev == 2 ? 0 : (prev + 1) as ExpansionLevel

            return ret;
        });
    };

    const completedCount = todoList?.items.filter(i => i.status === 'completed').length ?? 0;
    const totalCount = todoList?.items.length ?? 0;
    const position = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
    const oneOfN = completedCount > 0 ? `${completedCount} de ${totalCount} completos` : 'Iniciando...'
    const progressPct = `${oneOfN} - ${position.toFixed(2)}%`;
    const progressPctSmall = `${position.toFixed(2)}%`;

    const currentItem = todoList?.currentWorkingItemKey
        ? todoList.items.find(i => i.key === todoList.currentWorkingItemKey)
        : null;
    const todoItemsForLevel2 = todoList?.items.slice(0, MAX_TODO_ITEMS_LEVEL2) ?? [];
    const hasMoreItems = (todoList?.items.length ?? 0) > MAX_TODO_ITEMS_LEVEL2;

    const renderTodoItems = (items: TodoItem[]) => (
        <Stack gap={0.5} sx={{ py: 0.5 }}>
            {items.map((item) => (
                <Box
                    key={item.key}
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 0.75,
                        py: 0.25,
                        opacity: item.status === 'completed' ? 0.5 : 1,
                    }}
                >
                    <Box sx={{ mt: '1px', flexShrink: 0, color: statusColor(item.status) }}>
                        <StatusIcon status={item.status} />
                    </Box>
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                fontSize: '0.78rem',
                                lineHeight: 1.3,
                                textDecoration: item.status === 'completed' ? 'line-through' : 'none',
                            }}
                        >
                            {item.label}
                        </Typography>
                        {item.detail && (
                            <Typography
                                variant="caption"
                                sx={{ display: 'block', fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.3 }}
                            >
                                {item.detail}
                            </Typography>
                        )}
                    </Box>
                </Box>
            ))}
        </Stack>
    );

    return (
        <Box
            ref={boxRef}
            data-subagent-id={subAgent.id}
            sx={{
                border: `1px solid ${isRunning ? theme.palette.primary.dark : theme.palette.divider}`,
                borderRadius: '8px',
                my: compact ? 0 : 1.5,
                overflow: 'hidden',
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                transition: 'border-color 0.3s ease',
                position: 'relative',
            }}
        >
            {isRunning && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                        animation: 'subagent-progress 2s ease-in-out infinite',
                        '@keyframes subagent-progress': {
                            '0%': { transform: 'translateX(-100%)' },
                            '100%': { transform: 'translateX(100%)' },
                        },
                    }}
                />
            )}

            {/* Header — always visible */}
            {(isRunning || todoList) && (
                <LinerBackgroundProgress
                    value={position}
                    completedColor="rgba(120,120,120,0.2)"
                    unCompletedColor="rgba(120,120,120,0.05)"
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 1,
                            paddingTop: 0.5,
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                            width: '100%'
                        }}
                    >

                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: !compact ? 1 : 0,
                                cursor: 'pointer',
                                overflow: 'hidden',
                                minWidth: '100%'
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ display: 'inline-flex', flexWrap: 'nowrap', alignItems: 'center', overflow: 'hidden', zIndex: 1, width: '100%', flex: 1 }}>
                                <Stack direction={"row"} sx={{ alignItems: 'center', display: 'inline-flex', minWidth: level === 0 ? '22%' : '60%' }} gap={1}>
                                    {!compact && <SmartToy sx={{ fontSize: 18, flexShrink: 0, color: isRunning ? theme.palette.primary.main : theme.palette.success.main }} />}
                                    {isRunning ? (
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <HourglassEmpty sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                                        </Stack>
                                    ) : (
                                        <CheckCircle sx={{ fontSize: 14, color: theme.palette.success.main }} />
                                    )}
                                    <Typography variant="body1" sx={{ overflow: 'hidden', textAlign: 'left', fontWeight: 500, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                                        {subAgent.name ?? subAgent.id}
                                    </Typography>


                                </Stack>

                                {/* Todo summary inline at level 0  */}

                                <Box onClick={cycleLevel} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'start', minWidth: level === 0 ? '60%' : compact ? '40%' : '25%', minHeight: '25px' }}>
                                    {(todoList && (totalCount > 0)) && (
                                        <Stack direction={"row"}
                                            sx={{
                                                color: 'text.secondary',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'start',
                                                ml: 1,
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    color: theme.palette.text.secondary,
                                                    fontWeight: 500,
                                                    letterSpacing: 0.4,
                                                    px: 1,
                                                    mb: 0.5,
                                                }}
                                            >
                                                {compact ? `${progressPctSmall}` : `[${progressPct}]`}
                                            </Typography>

                                            {currentItem && !compact && level === 0 && (
                                                <Typography
                                                    component="span"
                                                    variant="body1"
                                                    sx={{ color: 'primary.main', ml: 3 }}
                                                >
                                                    {currentItem?.label}
                                                </Typography>
                                            )}
                                        </Stack>
                                    )}
                                </Box>

                                {!compact && <Stack onClick={cycleLevel} direction="row" alignItems="center" justifyContent={"end"} spacing={0} sx={{ minWidth: '15%', zIndex: 1, flexShrink: 0 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', width: '60%', textAlign: 'end' }}>
                                        {level === 0
                                            ? 'Tarefas'
                                            : level === 1
                                                ? '+ Detalhes'
                                                : 'Minimizar'}
                                    </Typography>

                                    {level === 0
                                        ? <ExpandMore sx={{ fontSize: '18px', p: 0, m: 0 }} />
                                        : level === 1
                                            ? <KeyboardDoubleArrowDown sx={{ fontSize: '18px', p: 0, m: 0 }} />
                                            : <KeyboardDoubleArrowUp sx={{ fontSize: '18px', p: 0, m: 0 }} />}

                                </Stack>}
                            </Stack>


                        </Box>
                    </Box>

                </LinerBackgroundProgress>
            )}



            {/* Level 1: Full todo list */}
            <Collapse in={level === 1} timeout="auto" unmountOnExit>
                <Box
                    sx={{
                        borderTop: `1px solid ${theme.palette.divider}`,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        px: 2,
                        pb: 1,
                        '&::-webkit-scrollbar': { width: '4px' },
                        '&::-webkit-scrollbar-thumb': { background: theme.palette.grey[700], borderRadius: '4px' },
                    }}
                >
                    {todoList ? renderTodoItems(todoList.items) : (
                        <Typography variant="caption" sx={{ color: 'text.disabled', py: 1, display: 'block' }}>
                            No tasks yet
                        </Typography>
                    )}
                </Box>
            </Collapse>

            {/* Level 2: Limited todo list + full agent content */}
            <Collapse in={level === 2} timeout="auto" unmountOnExit>
                <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
                    {/* Capped todo list */}
                    {todoList && todoList.items.length > 0 && (
                        <Box sx={{ px: 2, pb: 0.5, maxHeight: '160px', overflowY: 'auto' }}>
                            {renderTodoItems(todoItemsForLevel2)}
                            {hasMoreItems && (
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', pl: 2.5 }}>
                                    +{todoList.items.length - MAX_TODO_ITEMS_LEVEL2} more
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Agent content */}
                    <Box
                        sx={{
                            borderTop: todoList && todoList.items.length > 0 ? `1px solid ${theme.palette.divider}` : 'none',
                            maxHeight: '400px',
                            overflowY: 'auto',
                            px: 2,
                            pb: 1,
                            '&::-webkit-scrollbar': { width: '4px' },
                            '&::-webkit-scrollbar-thumb': { background: theme.palette.grey[700], borderRadius: '4px' },
                        }}
                    >
                        <MessageAgent
                            message={subAgent.interaction}
                            showSubAgents={false}
                            onGoing={isRunning}
                            onLinkClicked={onLinkClicked}
                            appendWaitingPart={isRunning}
                            isConnectionOpen={true}
                            preview={false}
                            container={`subagent-${subAgent.id}`}
                        />
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

export default SubAgentBox;
